import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import aiAgentService from '../services/aiAgentService';

const initialState = {
  agentProfile: null,
  conversationHistory: [],
  currentMessage: '',
  isLoading: false,
  isSending: false,
  isSuccess: false,
  isError: false,
  message: ''
};

function makePendingId() {
  return `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeConversationMessage(message) {
  if (!message) return null;
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    timestamp: message.timestamp,
  };
}

export const getAgentProfile = createAsyncThunk(
  'aiAgent/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await aiAgentService.getAgentProfile();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get agent profile');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'aiAgent/sendMessage',
  async (message, { rejectWithValue }) => {
    try {
      const response = await aiAgentService.sendMessage(message);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send message');
    }
  }
);

export const updateAgentSettings = createAsyncThunk(
  'aiAgent/updateSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await aiAgentService.updateAgentSettings(settings);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update settings');
    }
  }
);

export const getConversationHistory = createAsyncThunk(
  'aiAgent/getConversationHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await aiAgentService.getConversationHistory();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get conversation history');
    }
  }
);

const aiAgentSlice = createSlice({
  name: 'aiAgent',
  initialState,
  reducers: {
    setCurrentMessage: (state, action) => {
      state.currentMessage = action.payload;
    },
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAgentProfile.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(getAgentProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.agentProfile = action.payload;
      })
      .addCase(getAgentProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Send Message — optimistically add user message immediately
      .addCase(sendMessage.pending, (state, action) => {
        state.isSending = true;
        state.isError = false;
        state.message = '';
        const pendingId = makePendingId();
        action.meta.pendingId = pendingId;
        state.conversationHistory.push({
          role: 'user',
          content: action.meta.arg,
          timestamp: new Date().toISOString(),
          _pendingId: pendingId,
        });
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = '';
        state.currentMessage = '';
        const persistedUser = normalizeConversationMessage(action.payload.userMessage);
        const persistedAssistant = normalizeConversationMessage(action.payload.assistantMessage);

        if (persistedUser) {
          const pendingIndex = state.conversationHistory.findIndex((item) => item._pendingId);
          if (pendingIndex >= 0) {
            state.conversationHistory[pendingIndex] = persistedUser;
          } else {
            state.conversationHistory.push(persistedUser);
          }
        }

        if (persistedAssistant) {
          state.conversationHistory.push(persistedAssistant);
        }

        if (state.agentProfile) {
          state.agentProfile.learning_progress = action.payload.learningProgress;
          state.agentProfile.progress_dimensions = action.payload.progressDimensions || [];
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSending = false;
        state.isError = true;
        state.message = action.payload;
        // Keep the user's message visible but mark as failed
        const lastIdx = state.conversationHistory.length - 1;
        if (lastIdx >= 0 && state.conversationHistory[lastIdx]?._pendingId) {
          state.conversationHistory[lastIdx]._failed = true;
        }
      })
      // Update Agent Settings
      .addCase(updateAgentSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateAgentSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.agentProfile = { ...state.agentProfile, ...action.payload };
      })
      .addCase(updateAgentSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get Conversation History
      .addCase(getConversationHistory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getConversationHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.conversationHistory = action.payload.map((message) => normalizeConversationMessage(message));
      })
      .addCase(getConversationHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  }
});

export const { setCurrentMessage, reset } = aiAgentSlice.actions;
export default aiAgentSlice.reducer;
